import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'
import { useCurrentUser } from '@/hooks/use-auth'
import { invalidatePaymentQueries } from '@/lib/invalidate-payment-queries'

export function useFinanceCapabilities() {
  const { data: user } = useCurrentUser()
  return useQuery({
    queryKey: ['invoice-capabilities', user?.id],
    enabled: !!user,
    queryFn: () => apiClient.get<{ financeDirectReconcile: boolean }>('/invoices/capabilities'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

export type ConfirmationInput = {
  invoiceId: string
  requestId: string
} & ({
  mode: 'NEW_RECEIPT'
  amount: string
  accountRef?: string
  sourceKind: 'BANK' | 'CASH' | 'INTERNAL'
  bankFlowNo?: string | null
  paidAt: string
  payerNote?: string | null
} | {
  mode: 'EXISTING_RECEIPT'
  paymentId: string
})

export interface ConfirmationResult {
  invoiceId: string
  paymentId: string
  amount: string
  reconciledAmount: string
  receivableAmount: string
  settlementStatus: string
  overpaidToAccount: string
  alreadyProcessed: boolean
}

export function useConfirmReconciliation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, ...body }: ConfirmationInput) =>
      apiClient.post<ConfirmationResult>(`/invoices/${invoiceId}/confirm-reconciliation`, body),
    onSuccess: () => invalidatePaymentQueries(client),
  })
}
